import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HiOutlinePlus, HiOutlineX } from "react-icons/hi";
import { twMerge } from "tailwind-merge";

import { HorizontalScrollWrapper } from "../../../components/shared/HorizontalScrollWrapper/HorizontalScrollWrapper";
import Button from "../../../components/shared/Button/Button";
import Divider from "../../../components/shared/Divider/Divider";
import Checkbox from "../../../components/shared/Checkbox";
import { useIndicators } from "../hooks/useIndicators";
import {
  useSwingLibrarySettings,
  useUpdateSwingLibrarySettings,
} from "../hooks/useSwingLibrarySettings";
import SelectMulti from "../../../components/shared/Select/SelectMulti";

const POSITION_LABEL_MAP = {
  ADR: "ADR (Address)",
  BAH: "BAH (Backswing Lead Arm Parallel)",
  BCH: "BCH (Backswing Club Shaft Horizontal)",
  DAH: "DAH (Downswing Lead Arm Parallel)",
  DCH: "DCH (Downswing Club Shaft Horizontal)",
  FCH: "FCH (Follow Through Club Shaft Horizontal)",
  FIN: "FIN (Finish)",
  IMP: "IMP (Impact)",
  TOP: "TOP (Top of Backswing)",
};

const POSITION_ORDER = Object.keys(POSITION_LABEL_MAP);

const isPositionKey = (value) =>
  !!value && Object.prototype.hasOwnProperty.call(POSITION_LABEL_MAP, value);

export function IndicatorSelectList({ isOpen, onClose }) {
  const { data: swingLibrarySettings } = useSwingLibrarySettings();
  const updateSwingLibrarySettings = useUpdateSwingLibrarySettings();

  const { data: indicators = [], isLoading: indicatorsLoading } =
    useIndicators();

  const [tagFilter, setTagFilter] = useState("All");
  const [positionFilter, setPositionFilter] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // seed selected on open/values change
  useEffect(() => {
    setSelectedIds(
      swingLibrarySettings?.savedIndicators?.map((v) => v.id) ?? []
    );
  }, [swingLibrarySettings?.savedIndicators]);

  const allItemsFlat = useMemo(() => {
    return indicators.reduce((acc, category) => acc.concat(category.items), []);
  }, [indicators]);

  // Matches current positionFilter against indicatorGrouping
  const itemMatchesPosition = useCallback(
    (item) => {
      if (positionFilter.length === 0) return true;

      const groups = item.indicatorGrouping || [];
      if (!groups.length) return false;

      return positionFilter.some((filterVal) => {
        if (filterVal === "BCKSWNG") return groups.includes("Backswing");
        if (filterVal === "DWNSWNG") return groups.includes("Downswing");
        if (filterVal === "FTHRU")
          return (
            groups.includes("Follow Through") || groups.includes("FollowThru")
          );
        return groups.includes(filterVal);
      });
    },
    [positionFilter]
  );

  const visibleCategories = useMemo(() => {
    const inTag = (category) =>
      tagFilter === "All" ? true : category.category === tagFilter;

    return indicators
      .filter(inTag)
      .map((category) => ({
        ...category,
        items: category.items.filter(itemMatchesPosition),
      }))
      .filter((category) => category.items.length > 0);
  }, [indicators, tagFilter, itemMatchesPosition]);

  const positionOptions = useMemo(() => {
    if (!allItemsFlat.length) return [];

    const presentCodes = new Set();

    allItemsFlat.forEach((item) => {
      const pos = item.positionOrPhase;
      const groups = item.indicatorGrouping || [];

      if (isPositionKey(pos)) {
        presentCodes.add(pos);
      }

      groups.forEach((group) => {
        if (isPositionKey(group)) {
          presentCodes.add(group);
        }
      });
    });

    const orderedPositionOptions = POSITION_ORDER.filter((code) =>
      presentCodes.has(code)
    ).map((code) => ({
      text: POSITION_LABEL_MAP[code],
      value: code,
    }));

    let hasBackswing = false;
    let hasDownswing = false;
    let hasFollowThrough = false;

    allItemsFlat.forEach((item) => {
      (item.indicatorGrouping || []).forEach((group) => {
        if (group === "Backswing") hasBackswing = true;
        if (group === "Downswing") hasDownswing = true;
        if (group === "Follow Through" || group === "FollowThru") {
          hasFollowThrough = true;
        }
      });
    });

    if (hasBackswing) {
      orderedPositionOptions.push({
        text: "BCKSWNG (Back Swing)",
        value: "BCKSWNG",
      });
    }

    if (hasDownswing) {
      orderedPositionOptions.push({
        text: "DWNSWNG (Down Swing)",
        value: "DWNSWNG",
      });
    }

    if (hasFollowThrough) {
      orderedPositionOptions.push({
        text: "FTHRU (Follow Through)",
        value: "FTHRU",
      });
    }

    return orderedPositionOptions;
  }, [allItemsFlat]);

  const toggleId = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat(id)
    );

  const addMany = (ids) =>
    setSelectedIds((prev) =>
      prev.concat(ids.filter((id) => !prev.includes(id)))
    );

  const handleSelectAll = () => {
    if (positionFilter.length === 0 && tagFilter === "All") {
      addMany(allItemsFlat.map((item) => item.id));
      return;
    }
    const ids = visibleCategories.flatMap((category) =>
      category.items.map((item) => item.id)
    );
    addMany(ids);
  };

  const handleSelectAllCategory = (items) => {
    const ids = items.filter(itemMatchesPosition).map((item) => item.id);
    addMany(ids);
  };

  const handleApply = async () => {
    const selectedItems = allItemsFlat.filter((item) =>
      selectedIds.includes(item.id)
    );
    await updateSwingLibrarySettings.mutateAsync({
      savedIndicators: selectedItems,
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      aria-label="Add Data"
      aria-modal="true"
      className="border-gray200 font-manrope fixed top-0 right-0 z-[2000] flex h-screen w-full max-w-96 flex-col border-l bg-white p-6"
      role="dialog"
    >
      {/* Header */}
      <div className="relative flex flex-col gap-1">
        <div className="text-xl font-semibold">Add Data</div>

        <div className="text-gray600 text-sm">
          Apply or remove data to the table
        </div>

        <button
          aria-label="Close"
          className="absolute top-0 right-0 w-fit rounded-sm p-1"
          onClick={onClose}
        >
          <HiOutlineX className="text-gray500 size-5" />
        </button>
      </div>

      {/* Tag list */}
      <div className="mt-6">
        <HorizontalScrollWrapper style={{ width: 300 }}>
          {[{ category: "All", items: [] }, ...indicators].map((el) => {
            const active = tagFilter === el.category;
            return (
              <div
                className={twMerge(
                  "text-gray500 mr-2 inline-flex cursor-pointer rounded-full px-3 py-2 text-sm font-semibold select-none",
                  active && "bg-primary50 text-primary900"
                )}
                key={el.category}
                onClick={() => setTagFilter(el.category)}
              >
                {el.category}
              </div>
            );
          })}
        </HorizontalScrollWrapper>
      </div>

      {/* Position filter */}
      <div className="mt-4">
        <SelectMulti
          header="Position"
          onChange={(val) => {
            const value = String(val);
            setPositionFilter((prev) =>
              prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
            );
          }}
          onClear={() => setPositionFilter([])}
          options={positionOptions}
          placeholder="Select position or phase"
          values={positionFilter}
        />
      </div>

      <Divider className="mt-4" color="gray200" type="horizontal" />

      {/* Categories list */}
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto pt-4">
        {indicatorsLoading && (
          <div className="text-gray500 text-sm">Loading indicators…</div>
        )}

        {!indicatorsLoading &&
          visibleCategories.map((cat) => (
            <div key={cat.category}>
              <div className="flex items-center justify-between">
                <div className="text-gray700 text-sm">{cat.category}</div>
                <button
                  className="flex items-center gap-1"
                  onClick={() => handleSelectAllCategory(cat.items)}
                >
                  <HiOutlinePlus className="text-primary700 hover:text-primary600 size-4" />
                  <span className="text-primary700 hover:text-primary600 text-sm font-semibold">
                    Add all
                  </span>
                </button>
              </div>

              <div className="mt-2 flex flex-col gap-3">
                {cat.items.map((item) => {
                  const isChecked = selectedIds.includes(item.id);
                  return (
                    <button
                      className="flex w-full items-center gap-2"
                      key={item.id}
                      onClick={() => toggleId(item.id)}
                      title={item?.description || "No description available"}
                    >
                      <Checkbox isChecked={isChecked} size="sm" />

                      <span className="text-left text-sm text-black">
                        {item.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
      </div>

      {/* Footer */}
      <div className="mt-auto">
        <Divider color="gray200" type="horizontal" />

        <div className="flex items-center justify-between pt-4">
          <button
            className={twMerge(
              "text-base font-semibold",
              selectedIds.length === allItemsFlat.length
                ? "text-gray300"
                : "text-primary700"
            )}
            onClick={handleSelectAll}
          >
            Include All
          </button>

          <div className="flex gap-2">
            <Button color="gray100" onClick={onClose} size="md">
              Cancel
            </Button>

            <Button
              isDisabled={selectedIds.length === 0}
              onClick={handleApply}
              size="md"
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
